import { useList, useGo, useDelete, useCustomMutation } from '@refinedev/core';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { notify } from '@/lib/notify';
import { Library, Plus, Trash2, BookOpen, Eye, EyeOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { useConfirm } from '@/hooks/use-confirm';
import { getBookTitle, type Book, BOOK_FORMAT_OPTIONS } from './types';
import { BookProcessingStatusBadge } from './components/BookProcessingStatus';
import { PricingTiersList } from '@/components/shared/PricingTiersList';
import type { PricingTier } from '@/components/shared/PricingTiersTooltip';
import { resolveApiUrl } from '@/providers/api-url';
import { resolveFileUrl } from '@/lib/file-url';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

const API_URL = resolveApiUrl();

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const LANGUAGE_LABELS: Record<string, string> = {
  ru: 'Русский',
  en: 'English',
  uz: "O'zbek",
  kz: 'Қазақ',
};

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function BooksList() {
  const go = useGo();
  const { mutate: deleteOne } = useDelete();
  const { mutate: publishMutation } = useCustomMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  useScrollRestoration();

  const [page, setPage] = usePersistedState('books:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('books:sort', { field: 'createdAt', order: 'desc' });

  const [searchInput, setSearchInput] = usePersistedState('books:search', '');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const [formatFilter, setFormatFilter] = usePersistedState('books:formatFilter', 'all');
  const [languageFilter, setLanguageFilter] = usePersistedState('books:languageFilter', 'all');
  const [publishedFilter, setPublishedFilter] = usePersistedState('books:publishedFilter', 'all');

  // Сброс страницы при изменении фильтров/поиска
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, formatFilter, languageFilter, publishedFilter, setPage]);

  const queryFilters = useMemo(() => {
    const filters: Array<{ field: string; operator: 'eq' | 'contains'; value: string }> = [];
    // Пустая строка тоже должна триггерить refetch (сброс поиска).
    filters.push({ field: 'search', operator: 'contains', value: debouncedSearch.trim() });
    if (formatFilter !== 'all') {
      filters.push({ field: 'format', operator: 'eq', value: formatFilter });
    }
    if (languageFilter !== 'all') {
      filters.push({ field: 'languageCode', operator: 'eq', value: languageFilter });
    }
    if (publishedFilter !== 'all') {
      filters.push({ field: 'isPublished', operator: 'eq', value: publishedFilter });
    }
    return filters;
  }, [debouncedSearch, formatFilter, languageFilter, publishedFilter]);

  const listResult = useList<Book>({
    resource: 'books',
    pagination: { currentPage: page, pageSize: PAGE_SIZE, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = useMemo(
    () => listResult.result.data ?? [],
    [listResult.result.data],
  );
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetch = listResult.query.refetch;

  const bookIdsWithTier = useMemo(
    () => data.filter((b) => b.pricingTierId !== null).map((b) => b.id),
    [data],
  );

  const tierQueries = useQueries({
    queries: bookIdsWithTier.map((bookId) => ({
      queryKey: ['book-pricing-tier', bookId],
      queryFn: async (): Promise<PricingTier[]> => {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(
          `${API_URL}/pricing-tiers/admin?bookId=${bookId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
          },
        );
        if (!res.ok) {
          return [];
        }
        const json = await res.json();
        return Array.isArray(json?.data) ? (json.data as PricingTier[]) : [];
      },
      staleTime: 30_000,
    })),
  });

  const tiersByBookId = useMemo(() => {
    const map = new Map<number, PricingTier[]>();
    bookIdsWithTier.forEach((bookId, idx) => {
      const result = tierQueries[idx]?.data;
      if (result && result.length > 0) {
        map.set(bookId, result);
      }
    });
    return map;
  }, [bookIdsWithTier, tierQueries]);

  const handleDelete = useCallback(
    async (record: Book) => {
      const ok = await confirm({
        title: `Удалить книгу «${getBookTitle(record)}»?`,
        description: 'Действие необратимо. Будут удалены главы, переводы и прогресс пользователей.',
        variant: 'destructive',
        confirmText: 'Удалить',
        cancelText: 'Отмена',
      });
      if (!ok) { return; }
      deleteOne(
        { resource: 'books', id: record.id },
        {
          onSuccess: () => {
            refetch();
          },
          onError: (error: { message?: string }) => {
            notify.error(error.message || 'Не удалось удалить книгу');
          },
        },
      );
    },
    [confirm, deleteOne, refetch],
  );

  const handleBulkPublish = useCallback(
    (items: Book[], isPublished: boolean) => {
      const targets = items.filter((b) => b.isPublished !== isPublished);
      if (targets.length === 0) { return; }
      let completed = 0;
      targets.forEach((book) => {
        publishMutation(
          {
            url: `books/${book.id}/publish`,
            method: 'post',
            values: { isPublished },
          },
          {
            onSettled: () => {
              completed += 1;
              if (completed === targets.length) { refetch(); }
            },
          },
        );
      });
    },
    [publishMutation, refetch],
  );

  const handleBulkDelete = useCallback(
    async (items: Book[]) => {
      if (items.length === 0) { return; }
      const ok = await confirm({
        title: `Удалить ${items.length} книг(и)?`,
        description: 'Действие необратимо. Будут удалены главы, переводы и прогресс пользователей.',
        variant: 'destructive',
        confirmText: 'Удалить',
        cancelText: 'Отмена',
      });
      if (!ok) { return; }
      let completed = 0;
      let failures = 0;
      items.forEach((book) => {
        deleteOne(
          { resource: 'books', id: book.id },
          {
            onError: () => { failures += 1; },
            onSettled: () => {
              completed += 1;
              if (completed === items.length) {
                if (failures > 0) {
                  notify.error(`Не удалось удалить ${failures} из ${items.length} книг(и)`);
                }
                refetch();
              }
            },
          },
        );
      });
    },
    [confirm, deleteOne, refetch],
  );

  const columns = useMemo<Column<Book>[]>(
    () => [
      {
        key: 'cover',
        header: '',
        width: '64px',
        render: (_, record) =>
          record.coverFileUrl ? (
            <img src={resolveFileUrl(record.coverFileUrl)} alt="" loading="lazy" className="h-12 w-9 rounded object-cover" />
          ) : (
            <div className="flex h-12 w-9 items-center justify-center rounded bg-muted">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </div>
          ),
      },
      {
        key: 'slug',
        header: 'Название',
        sortable: true,
        render: (_, record) => (
          <div className="min-w-0">
            <p className="truncate font-medium">{getBookTitle(record)}</p>
            <p className="truncate text-xs text-muted-foreground">{record.slug}</p>
          </div>
        ),
      },
      {
        key: 'format',
        header: 'Формат',
        sortable: true,
        width: '110px',
        render: (value) => <Badge variant="outline">{String(value)}</Badge>,
      },
      {
        key: 'languageCode',
        header: 'Язык',
        sortable: true,
        width: '110px',
        render: (value) => {
          const code = String(value).toLowerCase();
          const label = LANGUAGE_LABELS[code] ?? code.toUpperCase();
          return (
            <Badge variant="outline" className="gap-1 font-normal">
              <span className="text-xs font-semibold uppercase text-muted-foreground">{code}</span>
              <span className="text-sm">{label}</span>
            </Badge>
          );
        },
      },
      {
        key: 'isPublished',
        header: 'Публикация',
        sortable: true,
        width: '140px',
        render: (_, record) =>
          record.isPublished ? (
            <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
              Опубликована
            </Badge>
          ) : (
            <Badge variant="secondary">Черновик</Badge>
          ),
      },
      {
        key: 'processingStatus',
        header: 'Обработка',
        width: '160px',
        render: (_, record) => (
          <BookProcessingStatusBadge
            status={record.processingStatus}
            error={record.processingError}
          />
        ),
      },
      {
        key: 'pricingTier',
        header: 'Тариф',
        render: (_, record) => (
          <PricingTiersList
            tiers={tiersByBookId.get(record.id)}
            onTierClick={(tier) =>
              go({
                to: `/books/edit/${record.id}`,
                query: { tab: 'pricing', tier: tier.id },
              })
            }
          />
        ),
      },
      {
        key: 'createdAt',
        header: 'Создана',
        sortable: true,
        width: '120px',
        render: (value) => (
          <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: '64px',
        render: (_, record) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              void handleDelete(record);
            }}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [handleDelete, tiersByBookId, go],
  );

  const bulkActions: BulkAction<Book>[] = useMemo(
    () => [
      {
        label: 'Опубликовать',
        icon: <Eye className="h-4 w-4" />,
        onClick: (items) => handleBulkPublish(items, true),
        showOnlyWhenSelected: true,
      },
      {
        label: 'Снять с публикации',
        icon: <EyeOff className="h-4 w-4" />,
        onClick: (items) => handleBulkPublish(items, false),
        variant: 'outline',
        showOnlyWhenSelected: true,
      },
      {
        label: 'Удалить',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: (items) => { void handleBulkDelete(items); },
        variant: 'destructive',
        showOnlyWhenSelected: true,
      },
    ],
    [handleBulkPublish, handleBulkDelete],
  );

  const handleRowClick = useCallback(
    (record: Book) => {
      go({ to: `/books/edit/${record.id}` });
    },
    [go],
  );

  return (
    <div className="space-y-6">
      <FormHeader
        title="Книги"
        description="Библиотека: электронные книги и аудиокниги"
        sticky
        icon={<Library className="h-5 w-5" />}
        actions={
          <Button onClick={() => go({ to: '/books/create' })}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить книгу
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Формат" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все форматы</SelectItem>
            {BOOK_FORMAT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={languageFilter} onValueChange={setLanguageFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Язык" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все языки</SelectItem>
            <SelectItem value="ru">Русский</SelectItem>
            <SelectItem value="uz">O&apos;zbek</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="kz">Қазақ</SelectItem>
          </SelectContent>
        </Select>

        <Select value={publishedFilter} onValueChange={setPublishedFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Статус" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="true">Опубликованные</SelectItem>
            <SelectItem value="false">Черновики</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={setSearchInput}
        searchValue={searchInput}
        searchPlaceholder="Поиск по названию или slug..."
        onRowClick={handleRowClick}
        emptyMessage="Нет книг"
        sort={sort}
        onSortChange={setSort}
        selectable
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}
